import { createAsyncThunk, createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { RootState } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import _ from "lodash";
import MessagingAPIService from "messaging/_api/messaging-api-service";
import ForumQuerySchema, { ForumQueryJSON } from "messaging/forums/_api/queries/_schemas/forum-query-schema";
import ForumQuery, {
  ForumFilterParameters,
  ForumOrderField,
  ForumOrderParameters,
} from "messaging/forums/_api/queries/forum-query";
import Forum from "messaging/forums/forum";
import { ForumJSON } from "messaging/forums/_schemas/forum-schema";
import Topic from "messaging/forums/topics/topic";
import Session from "users/__session/session";

type ForumStoreState = {
  byId: {
    entries: Record<string, ForumJSON>;
    loading: Record<string, boolean>;
    error: Record<string, string | null>;
  };
  byTopic: {
    entries: Record<string, ForumJSON[]>;
    loading: Record<string, boolean>;
    error: Record<string, string | null>;
  };
  byQuery: {
    entries: Record<string, ForumJSON[]>;
    loading: Record<string, boolean>;
    error: Record<string, string | null>;
  };
  interfaceQueries: {
    entries: Record<string, ForumQueryJSON>;
  };
};

const initialState: ForumStoreState = {
  byId: {
    entries: {},
    loading: {},
    error: {},
  },
  byTopic: {
    entries: {},
    loading: {},
    error: {},
  },
  byQuery: {
    entries: {},
    loading: {},
    error: {},
  },
  interfaceQueries: {
    entries: {},
  },
};

export const populateForums = createAsyncThunk<
  ForumJSON[],
  {
    session: Session;
    forumIds: Guid[];
  }
>(
  "forums/getForumsByIds",
  async (
    {
      session,
      forumIds,
    }: {
      session: Session;
      forumIds: Guid[];
    },
    thunkAPI
  ) => {
    if (!forumIds.length) return [];

    const store: { forums: ForumStoreState } = thunkAPI.getState() as {
      forums: ForumStoreState;
    };
    const missingForumsIds = _.uniqWith(
      forumIds.filter((forumId) => !store.forums.byId.entries[forumId.value]),
      (a, b) => a.isEqualTo(b)
    );
    if (!missingForumsIds.length) return [];

    const apiService = new MessagingAPIService(session);
    const abortController = new AbortController();
    const onAbort = () => abortController.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      return await apiService.getForumsByIds(forumIds, abortController);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  }
);

export const populateForumsByTopic = createAsyncThunk(
  "forums/getForumsByTopic",
  async (
    {
      session,
      topic,
    }: {
      session: Session;
      topic: Topic;
    },
    thunkAPI
  ) => {
    const apiService = new MessagingAPIService(session);
    const abortController = new AbortController();
    const onAbort = () => abortController.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      const filterParameters = new ForumFilterParameters({
        context: topic.context,
        entityClass: topic.entityClass,
        entityId: topic.entityId,
      });
      const orderParameters = new ForumOrderParameters({
        orderBy: ForumOrderField.CreatedDate,
        orderDir: OrderDirection.Descending,
      });
      const paginationParameters = PaginationParameters.create({
        pageIndex: 0,
        pageSize: 100,
      });
      const request = new ForumQuery({
        filterParameters,
        orderParameters,
        paginationParameters,
      });
      return await apiService.getForums(request);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  }
);

export const populateForumsByQuery = createAsyncThunk<
  ForumJSON[],
  {
    session: Session;
    query: ForumQuery;
  }
>(
  "forums/getForumsByQuery",
  async (
    {
      session,
      query,
    }: {
      session: Session;
      query: ForumQuery;
    },
    thunkAPI
  ) => {
    const apiService = new MessagingAPIService(session);
    const abortController = new AbortController();
    const onAbort = () => abortController.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      return await apiService.getForums(query, abortController);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  }
);

const forumsSlice = createSlice({
  name: "forums",
  initialState,
  reducers: {
    addForum: (state, action: PayloadAction<Forum>) => {
      if (action.payload.id) {
        state.byId.entries[action.payload.id.value] = action.payload.toJSON();
      }
      if (action.payload.topic) {
        const topicKey = action.payload.topic.toString();
        if (!state.byTopic.entries[topicKey]) {
          state.byTopic.entries[topicKey] = [];
        }
        state.byTopic.entries[topicKey].push(action.payload.toJSON());
      }
    },
    updateForumInterfaceQuery: (
      state,
      action: PayloadAction<{
        interfaceName: string;
        query: ForumQuery;
      }>
    ) => {
      const { interfaceName, query } = action.payload;
      state.interfaceQueries.entries[interfaceName] = ForumQuerySchema.serialize(query);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(populateForumsByTopic.pending, (state, action) => {
      const topic = action.meta.arg.topic.toString();
      if (!topic) return;
      state.byTopic.loading[topic] = true;
      state.byTopic.error[topic] = null;
    });
    builder.addCase(populateForumsByTopic.fulfilled, (state, action) => {
      const topic = action.meta.arg.topic.toString();
      if (!topic) return;

      state.byTopic.loading[topic] = false;
      state.byTopic.entries[topic] = action.payload;

      for (const forum of action.payload) {
        if (forum.id) {
          state.byId.entries[forum.id] = forum;
        }
      }
    });
    builder.addCase(populateForumsByTopic.rejected, (state, action) => {
      const topic = action.meta.arg.topic.toString();
      if (!topic) return;
      state.byTopic.loading[topic] = false;
      state.byTopic.error[topic] = action.error.message || "Failed to load forums for topic";
    });

    builder.addCase(populateForums.pending, (state, action) => {
      const forumIds = action.meta.arg.forumIds;
      if (!forumIds.length) return;

      for (const id of forumIds) {
        state.byId.loading[id.value] = true;
        state.byId.error[id.value] = null;
      }
    });
    builder.addCase(populateForums.fulfilled, (state, action) => {
      const forumIds = action.meta.arg.forumIds;
      if (!forumIds.length) return;

      for (const forum of action.payload) {
        state.byId.entries[forum.id as string] = forum;
      }

      for (const id of forumIds) {
        state.byId.loading[id.value] = false;
        if (!state.byId.entries[id.value]) {
          state.byId.error[id.value] = "Forum not found";
        }
      }
    });
    builder.addCase(populateForums.rejected, (state, action) => {
      const forumIds = action.meta.arg.forumIds;
      if (!forumIds.length) return;

      for (const id of forumIds) {
        state.byId.loading[id.value] = false;
        state.byId.error[id.value] = action.error.message || "Error fetching forum info.";
      }
    });

    builder.addCase(populateForumsByQuery.pending, (state, action) => {
      const query = action.meta.arg.query.searchParams.toString();
      state.byQuery.loading[query] = true;
      state.byQuery.error[query] = null;
    });
    builder.addCase(populateForumsByQuery.fulfilled, (state, action) => {
      const query = action.meta.arg.query.searchParams.toString();
      state.byQuery.entries[query] = action.payload;
      state.byQuery.loading[query] = false;
      state.byQuery.error[query] = null;

      for (const forum of action.payload) {
        if (!forum.id) continue;
        state.byId.entries[forum.id] = forum;
        state.byId.loading[forum.id] = false;
        state.byId.error[forum.id] = null;
      }
    });
    builder.addCase(populateForumsByQuery.rejected, (state, action) => {
      const query = action.meta.arg.query.searchParams.toString();
      state.byQuery.loading[query] = false;
      state.byQuery.error[query] = action.payload as string;
    });
  },
});

export const { addForum, updateForumInterfaceQuery } = forumsSlice.actions;

export const getForumsByTopic = (topic: Topic) => (state: RootState) =>
  state.forums.byTopic.entries[topic.toString()]?.map((json) => Forum.fromJSON(json)) ?? undefined;
export const getIsLoadingForumsByTopic = (topic: Topic) => (state: RootState) =>
  state.forums.byTopic.loading[topic.toString()];
export const getErrorLoadingForumsByTopic = (topic: Topic) => (state: RootState) =>
  state.forums.byTopic.error[topic.toString()];

const selectForumsByIds = createSelector(
  [(state: RootState) => state.forums.byId.entries, (_: RootState, ids: Guid[]) => ids],
  (entries, ids) => {
    return ids
      .map((id) => entries[id.value])
      .filter(Boolean)
      .map((json) => Forum.fromJSON(json));
  }
);
export const getForumsByIds = (ids: Guid[]) => (state: RootState) => selectForumsByIds(state, ids);

const selectLoadingForumIds = createSelector([(state: RootState) => state.forums.byId.loading], (loading) => {
  return Object.keys(loading)
    .filter((forumId) => loading[forumId])
    .map((forumId) => Guid.fromJSON(forumId) as Guid);
});
export const getLoadingForumIds = (state: RootState) => selectLoadingForumIds(state);

export const getAreErrorsLoadingForumsByIds = (ids: Guid[]) => (state: RootState) =>
  ids.some((id) => state.forums.byId.error[id.value] !== null);

const selectForumsByQuery = createSelector(
  [(state: RootState) => state.forums.byQuery.entries, (_: RootState, query?: ForumQuery) => query],
  (entries, query) => {
    if (!query) return;
    return entries[query.searchParams.toString()]?.map((json) => Forum.fromJSON(json)) ?? undefined;
  }
);
export const getForumsByQuery = (query?: ForumQuery) => (state: RootState) => selectForumsByQuery(state, query);

const selectForumQueryByInterface = createSelector(
  [(state: RootState) => state.forums.interfaceQueries.entries, (_: RootState, interfaceName: string) => interfaceName],
  (entries, interfaceName) => {
    const queryJson = entries[interfaceName];
    if (!queryJson) return;
    return ForumQuery.fromJSON(queryJson) ?? undefined;
  }
);
export const getForumQueryByInterface = (interfaceName: string) => (state: RootState) =>
  selectForumQueryByInterface(state, interfaceName);

export const getIsLoadingForumsByQuery = (query?: ForumQuery) => (state: RootState) => {
  if (!query) return false;
  return state.forums.byQuery.loading[query.searchParams.toString()] ?? false;
};
export const getErrorLoadingForumsByQuery = (query?: ForumQuery) => (state: RootState) => {
  if (!query) return false;
  return state.forums.byQuery.error[query.searchParams.toString()] ?? false;
};

export type ForumError = {
  id: Guid;
  error: string;
};
const selectAllForumErrors = createSelector([(state: RootState) => state.forums.byId.error], (errors) => {
  return Object.keys(errors)
    .filter((forumId) => forumId && errors[forumId])
    .map((id) => {
      const error = errors[id];
      if (!error) return null;
      return { id: Guid.fromJSON(id) as Guid, error } as ForumError;
    })
    .filter((item): item is ForumError => item !== null);
});
export const getAllForumErrors = (state: RootState) => selectAllForumErrors(state);

export default forumsSlice;
